// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Deterministic transcendentals for the simulation core.
//
// WHY THIS FILE EXISTS
// --------------------
// A leaderboard score is admitted only after the server re-simulates the player's input
// trace, and the native client runs a C# copy of this same simulation. Both require that
// arithmetic reproduce exactly.
//
// IEEE-754 binary64 specifies `+ - * /` and `sqrt` exactly: every conforming runtime
// produces bit-identical results for them. It says nothing about `sin`, `cos` or `atan2`,
// and in practice V8, .NET and IL2CPP do NOT agree on their last bits. Calling the
// platform's library from the simulation would therefore make replays unverifiable.
//
// So the core carries its own, built only from the five exact operations, written in an
// explicit evaluation order that the C# sibling mirrors statement for statement.
//
// See 02-physics-and-feel-specification.md §11 (PH-081 to PH-087).
//
// ALGORITHMS
// ----------
// Cody-Waite argument reduction plus minimax kernel polynomials, the standard approach
// used by every serious libm. The polynomial coefficients are published minimax constants
// for binary64 — mathematical facts about the best-fitting polynomial of that degree, not
// anyone's intellectual property.
//
// NOT IMPLEMENTED, DELIBERATELY: `exp`, `log`, `pow`. The simulation does not need them.
// Exponential damping over a fixed timestep collapses to a constant multiplier computed
// once at load, so no per-step call is required. Add them only when something genuinely
// needs them — every transcendental is another thing that must stay identical in two
// languages forever.

/* eslint-disable no-bitwise */

// ---------------------------------------------------------------------------
// Exact operations. These are safe to use anywhere: IEEE-754 pins them down.
// ---------------------------------------------------------------------------

/** Square root. Correctly rounded and identical on every conforming runtime. */
export const sqrt = Math.sqrt

/** Absolute value. Exact. */
export function abs(x: number): number {
  return x < 0 ? -x : x
}

/** Floor. Exactly specified; C# `Math.Floor` agrees bit for bit. */
export const floor = Math.floor

/** Ceiling. Exactly specified. */
export const ceil = Math.ceil

/**
 * Round half away from zero.
 *
 * Deliberately NOT `Math.round`: JavaScript rounds halves toward +Infinity while C#'s
 * default `Math.Round` uses banker's rounding, so the two disagree on exactly the inputs
 * that matter. This definition is unambiguous in both languages.
 */
export function roundHalfAwayFromZero(x: number): number {
  return x >= 0 ? floor(x + 0.5) : -floor(-x + 0.5)
}

/** Clamp to an inclusive range. */
export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const PI = 3.141592653589793
export const TWO_PI = 6.283185307179586
export const HALF_PI = 1.5707963267948966

/** 2/π, for argument reduction. */
const INV_HALF_PI = 6.36619772367581382433e-1

/** π/2 split so that `n * PIO2_HI` is exact for the |n| we ever see. */
const PIO2_HI = 1.57079632673412561417e0
const PIO2_LO = 6.07710050650619224932e-11

// Minimax kernel coefficients for sin on [-π/4, π/4].
const S1 = -1.66666666666666324348e-1
const S2 = 8.33333333332248946124e-3
const S3 = -1.98412698298579493134e-4
const S4 = 2.75573137070700676789e-6
const S5 = -2.50507602534068634195e-8
const S6 = 1.58969099521155010221e-10

// Minimax kernel coefficients for cos on [-π/4, π/4].
const C1 = 4.16666666666666019037e-2
const C2 = -1.38888888888741095749e-3
const C3 = 2.48015872894767294178e-5
const C4 = -2.75573143513906633035e-7
const C5 = 2.08757232129817482790e-9
const C6 = -1.13596475577881948265e-11

// ---------------------------------------------------------------------------
// Kernels — valid on |x| <= π/4
// ---------------------------------------------------------------------------

function kernelSin(x: number): number {
  const z = x * x
  const v = z * x
  const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)))
  return x + v * (S1 + z * r)
}

function kernelCos(x: number): number {
  const z = x * x
  const r = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))))
  const hz = 0.5 * z
  const w = 1.0 - hz
  // The `((1-w) - hz)` term recovers the bits lost when `1 - hz` was rounded.
  return w + (((1.0 - w) - hz) + z * r)
}

/**
 * Cody-Waite reduction of `x` to `[-π/4, π/4]` plus a quadrant index.
 *
 * Splitting π/2 into a high and a low part keeps the subtraction exact for the argument
 * magnitudes a pinball machine ever produces — flipper angles, arc parameters and
 * accumulated spin, all comfortably inside |x| < 2^20.
 */
function reduceHalfPi(x: number): { y: number; quadrant: number } {
  const n = roundHalfAwayFromZero(x * INV_HALF_PI)
  const y = x - n * PIO2_HI - n * PIO2_LO
  // `n` can be negative; JS `%` keeps the sign, so normalise into 0..3.
  const quadrant = (((n % 4) + 4) % 4) | 0
  return { y, quadrant }
}

// ---------------------------------------------------------------------------
// sin / cos / tan
// ---------------------------------------------------------------------------

/** Sine. Deterministic across runtimes; accurate to <= 1 ULP over |x| < 2^20. */
export function sin(x: number): number {
  if (!Number.isFinite(x)) return Number.NaN
  const { y, quadrant } = reduceHalfPi(x)
  switch (quadrant) {
    case 0:
      return kernelSin(y)
    case 1:
      return kernelCos(y)
    case 2:
      return -kernelSin(y)
    default:
      return -kernelCos(y)
  }
}

/** Cosine. Deterministic across runtimes; accurate to <= 1 ULP over |x| < 2^20. */
export function cos(x: number): number {
  if (!Number.isFinite(x)) return Number.NaN
  const { y, quadrant } = reduceHalfPi(x)
  switch (quadrant) {
    case 0:
      return kernelCos(y)
    case 1:
      return -kernelSin(y)
    case 2:
      return -kernelCos(y)
    default:
      return kernelSin(y)
  }
}

/**
 * Sine and cosine together.
 *
 * The reduction is the expensive half, and the flipper needs both every step to place
 * its bat, so sharing it is worth a dedicated entry point.
 */
export function sinCos(x: number): { sin: number; cos: number } {
  if (!Number.isFinite(x)) return { sin: Number.NaN, cos: Number.NaN }
  const { y, quadrant } = reduceHalfPi(x)
  const s = kernelSin(y)
  const c = kernelCos(y)
  switch (quadrant) {
    case 0:
      return { sin: s, cos: c }
    case 1:
      return { sin: c, cos: -s }
    case 2:
      return { sin: -s, cos: -c }
    default:
      return { sin: -c, cos: s }
  }
}

// ---------------------------------------------------------------------------
// atan / atan2
// ---------------------------------------------------------------------------

// Endpoint values for the five reduction intervals, each split hi/lo to preserve bits.
const ATAN_HI = [
  4.63647609000806093515e-1, // atan(0.5)
  7.85398163397448278999e-1, // atan(1.0)
  9.82793723247329054082e-1, // atan(1.5)
  1.57079632679489655800e0, // atan(inf)
] as const
const ATAN_LO = [
  2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17,
  6.12323399573676603587e-17,
] as const

const T0 = 3.33333333333329318027e-1
const T1 = -1.99999999998764832476e-1
const T2 = 1.42857142725034663711e-1
const T3 = -1.11111104054623557880e-1
const T4 = 9.09088713343650656196e-2
const T5 = -7.69187620504482999495e-2
const T6 = 6.66107313738753120669e-2
const T7 = -5.83357013379057348645e-2
const T8 = 4.97687799461593236017e-2
const T9 = -3.65315727442169155270e-2
const T10 = 1.62858201153657823623e-2

/** Arctangent. Deterministic across runtimes. */
export function atan(x: number): number {
  if (Number.isNaN(x)) return Number.NaN
  if (!Number.isFinite(x)) return x > 0 ? HALF_PI : -HALF_PI

  const negative = x < 0
  let a = negative ? -x : x
  let id: number

  if (a >= 7.3e16) {
    // Beyond this the answer is π/2 to within a rounding error.
    return negative ? -ATAN_HI[3]! : ATAN_HI[3]!
  }

  if (a < 0.4375) {
    id = -1
  } else if (a < 1.1875) {
    if (a < 0.6875) {
      id = 0
      a = (2.0 * a - 1.0) / (2.0 + a)
    } else {
      id = 1
      a = (a - 1.0) / (a + 1.0)
    }
  } else if (a < 2.4375) {
    id = 2
    a = (a - 1.5) / (1.0 + 1.5 * a)
  } else {
    id = 3
    a = -1.0 / a
  }

  const z = a * a
  const w = z * z
  const s1 = z * (T0 + w * (T2 + w * (T4 + w * (T6 + w * (T8 + w * T10)))))
  const s2 = w * (T1 + w * (T3 + w * (T5 + w * (T7 + w * T9))))

  if (id < 0) {
    const r = a - a * (s1 + s2)
    return negative ? -r : r
  }

  const r = ATAN_HI[id]! - (a * (s1 + s2) - ATAN_LO[id]! - a)
  return negative ? -r : r
}

/**
 * Two-argument arctangent, result in (-π, π].
 *
 * NOTE: this is not in the simulation hot path and should not be put there. Arc
 * containment and angular ordering are tested with dot and cross products, which are
 * exact, cheaper, and free of branch-heavy quadrant logic. `atan2` exists for authoring,
 * diagnostics and the Builder.
 */
export function atan2(y: number, x: number): number {
  if (Number.isNaN(y) || Number.isNaN(x)) return Number.NaN

  if (x === 0) {
    if (y > 0) return HALF_PI
    if (y < 0) return -HALF_PI
    return 0 // atan2(0, 0) is defined as 0 here rather than left to platform convention
  }

  const a = atan(y / x)
  if (x > 0) return a
  return y >= 0 ? a + PI : a - PI
}

// ---------------------------------------------------------------------------
// Angle helpers
// ---------------------------------------------------------------------------

/** Wrap an angle into (-π, π]. Uses only exact operations. */
export function wrapAngle(a: number): number {
  let r = a - TWO_PI * roundHalfAwayFromZero(a / TWO_PI)
  if (r <= -PI) r += TWO_PI
  else if (r > PI) r -= TWO_PI
  return r
}

/** Degrees to radians. */
export function toRadians(degrees: number): number {
  return (degrees * PI) / 180
}

/** Radians to degrees. */
export function toDegrees(radians: number): number {
  return (radians * 180) / PI
}
